import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UploadResult } from '@/api/audits/types';
import { putFileToStorage, requestPresignedUrl } from '@/api/upload';
import { router } from '@/lib/utils/router';
import type { UploadSlot, UploadSlotMeta, UploadStore } from '@/store/types';

export type { UploadResult, UploadSlot, UploadSlotMeta, UploadStore };

// ─── Module-level registries (not Zustand state — functions can't be serialised) ─

const _promises = new Map<string, Promise<UploadResult>>();
const _actions = new Map<
  string,
  (result?: UploadResult) => Promise<void> | void
>();
const _finishCallbacks = new Map<
  string,
  (result: UploadResult) => Promise<void> | void
>();

const AUTO_DISMISS_ERROR_MS = 15_000;

export const useUploadStore = create<UploadStore>()(
  persist(
    set => ({
      slots: {},
      userId: null,

      enqueue: (file: File, meta: UploadSlotMeta) => {
        const id = crypto.randomUUID();

        if (meta.action) _actions.set(id, meta.action);
        if (meta.onFinish) _finishCallbacks.set(id, meta.onFinish);

        // Auto-capture the current page URL so the tray can navigate back after refresh
        let ctaUrl: string | undefined;
        try {
          const u = new URL(window.location.href);
          u.searchParams.set('slotId', id);
          ctaUrl = u.toString();
        } catch {
          // non-browser env (tests) — skip
        }

        set(s => ({
          slots: {
            ...s.slots,
            [id]: {
              id,
              filename: file.name,
              fileSize: file.size,
              status: 'uploading',
              progress: 0,
              startedAt: Date.now(),
              ...(meta.label !== undefined ? { label: meta.label } : {}),
              owner: meta.owner,
              hasAction: !!meta.action,
              metadata: {
                ...(ctaUrl ? { ctaUrl } : {}),
                ...(meta.metadata ?? {}),
              },
            },
          },
        }));

        const promise = requestPresignedUrl(file)
          .then(
            async ({ data: { id: fileId, url, originalUrl, bucketName } }) => {
              await putFileToStorage(url, file, pct => {
                set(s => {
                  const slot = s.slots[id];
                  if (slot?.status !== 'uploading') return s;
                  return {
                    slots: { ...s.slots, [id]: { ...slot, progress: pct } },
                  };
                });
              });
              return {
                id: fileId,
                url,
                originalUrl,
                bucketName,
              } as UploadResult;
            },
          )
          .then(result => {
            set(s => {
              const slot = s.slots[id];
              if (!slot) return s;
              return {
                slots: {
                  ...s.slots,
                  [id]: { ...slot, status: 'done', progress: 100, result },
                },
              };
            });
            const onFinish = _finishCallbacks.get(id);
            if (onFinish) void onFinish(result);
            return result;
          })
          .catch((err: unknown) => {
            const error = err instanceof Error ? err.message : 'Upload failed';
            set(s => {
              const slot = s.slots[id];
              if (!slot) return s;
              return {
                slots: {
                  ...s.slots,
                  [id]: { ...slot, status: 'error', error },
                },
              };
            });
            setTimeout(() => {
              useUploadStore.getState().removeSlot(id);
            }, AUTO_DISMISS_ERROR_MS);
            throw err instanceof Error ? err : new Error(error);
          })
          .finally(() => {
            _promises.delete(id);
          });

        _promises.set(id, promise);
        return id;
      },

      removeSlot: (id: string) => {
        _actions.delete(id);
        _finishCallbacks.delete(id);
        set(s => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [id]: _removed, ...rest } = s.slots;
          return { slots: rest };
        });
      },

      clearCompleted: () =>
        set(s => ({
          slots: Object.fromEntries(
            Object.entries(s.slots).filter(
              ([, slot]) => slot.status === 'uploading',
            ),
          ),
        })),

      clearAll: () => set({ slots: {} }),

      setUserId: (id: string | null) => set({ userId: id }),
    }),
    {
      name: 'upload-tray',
      // Only persist done slots — in-progress uploads cannot be resumed after refresh
      partialize: state => ({
        userId: state.userId,
        slots: Object.fromEntries(
          Object.entries(state.slots)
            .filter(([, slot]) => slot.status === 'done')
            // Strip hasAction: action closures don't survive a page refresh
            .map(([id, slot]) => [id, { ...slot, hasAction: false }]),
        ),
      }),
    },
  ),
);

/** Await the result of an enqueued upload. Safe to call from mutations/callbacks. */
export function awaitUpload(slotId: string): Promise<UploadResult> {
  const pending = _promises.get(slotId);
  if (pending) return pending;
  const slot = useUploadStore.getState().slots[slotId];
  if (slot?.status === 'done' && slot.result)
    return Promise.resolve(slot.result);
  return Promise.reject(
    new Error(`Upload slot "${slotId}" not found or already expired`),
  );
}

/**
 * Register (or update) a "continue" action for an existing slot after it was enqueued.
 * Useful when the action can only be captured after the upload starts.
 * Action receives the upload result when invoked.
 */
export function registerSlotAction(
  slotId: string,
  action: (result?: UploadResult) => Promise<void> | void,
  label?: string,
): void {
  _actions.set(slotId, action);
  set_hasAction(slotId, true, label);
}

function set_hasAction(slotId: string, hasAction: boolean, label?: string) {
  useUploadStore.setState(s => {
    const slot = s.slots[slotId];
    if (!slot) return s;
    return {
      slots: {
        ...s.slots,
        [slotId]: {
          ...slot,
          hasAction,
          ...(label !== undefined ? { label } : {}),
        },
      },
    };
  });
}

/** Invoke the registered action for a slot (called when user clicks the slot in the tray). */
export function invokeSlotAction(slotId: string): void {
  const action = _actions.get(slotId);
  const slot = useUploadStore.getState().slots[slotId];
  if (action) {
    void action(slot?.result);
    return;
  }
  // _actions is module-level and lost on page refresh — fall back to persisted ctaUrl
  const ctaUrl = slot?.metadata?.ctaUrl as string | undefined;
  if (!ctaUrl) return;
  // Use TanStack Router for internal URLs (no full reload); fall back to
  // window.location for external links.
  try {
    const target = new URL(ctaUrl, window.location.origin);
    if (target.origin === window.location.origin) {
      void router.navigate({
        to: target.pathname + target.search + target.hash,
      });
      return;
    }
  } catch {
    // malformed URL — fall through to full navigation
  }
  window.location.href = ctaUrl;
}

/** Return all slots whose owner matches the given id. */
export function getSlotsByOwner(ownerId: string): UploadSlot[] {
  return Object.values(useUploadStore.getState().slots).filter(
    s => s.owner === ownerId,
  );
}

/** Merge arbitrary data into a slot's metadata. Safe to call from anywhere. */
export function updateSlotMeta(
  slotId: string,
  meta: Record<string, unknown>,
): void {
  useUploadStore.setState(s => {
    const slot = s.slots[slotId];
    if (!slot) return s;
    return {
      slots: {
        ...s.slots,
        [slotId]: {
          ...slot,
          metadata: { ...(slot.metadata ?? {}), ...meta },
        },
      },
    };
  });
}
